import { InputPrice } from '@/components/element/Form/InputPrice';
import { ModalConfirm } from '@/components/element/modal/ModalConfirm';
import { usePurchaseOrderInvalidate } from '@/hooks/api/0570_purchase_order';
import { usePaymentSchedules, usePaymentSchedulesInvalidate } from '@/hooks/api/0680_payment_schedules';
import { useUpdatePaymentSchedule } from '@/hooks/api/0700_update_payment_schedule';
import { useDestroyPaymentSchedule } from '@/hooks/api/0710_destroy_payment_schedule';
import { PaymentSchedule } from '@/hooks/api/models';
import { PaymentScheduleLogic, TeamMemberLogic } from '@/hooks/api/models/logics';
import { toastApiError } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { useCurrentPurchaseOrder } from '@/providers/PurchaseOrderProvider';
import { useCurrentTeamMember } from '@/providers/TeamMemberProvider';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';

export const PaymentSchedulesElement = () => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  const { data: paymentSchedules } = usePaymentSchedules({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });
  const [delTarget, setDelTarget] = useState<PaymentSchedule>();
  const {
    mutate: destroyMutate,
    isSuccess: destroyIsSuccess,
    isPending: destroyIsPending,
    error,
  } = useDestroyPaymentSchedule();
  const { invalidate } = usePaymentSchedulesInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });
  const { invalidate: invalidatePurchaseOrder } = usePurchaseOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });

  const isEditable = useMemo(() => {
    if (meTeamMember && TeamMemberLogic.hasApproverRole(meTeamMember)) {
      return true;
    } else {
      return !_po_!.closed;
    }
  }, [meTeamMember, _po_]);

  useEffect(() => {
    if (destroyIsSuccess) {
      invalidate();
      invalidatePurchaseOrder();
    }
  }, [destroyIsSuccess]);

  useEffect(() => setShowLoading(destroyIsPending), [destroyIsPending]);
  useEffect(() => toastApiError(error), [error]);

  const onDeleteClick = () => {
    setShowLoading(true);
    destroyMutate({
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
      payment_schedule_id: delTarget!.id,
    });
  };

  return (
    <div className="table-responsive">
      <ModalConfirm
        modalId={`delConfirm`}
        okClicked={() => onDeleteClick()}
        message={
          '削除されたデータは元に戻せません。\n紐づくタスクが発行されている場合は削除ができないので、\n該当タスクを先に削除してください。'
        }
      />

      <table className="table align-items-center">
        <thead>
          <tr>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-10 text-end align-bottom border-end">
              請求月
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-15 text-end align-bottom">
              予定(税抜)
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-15 text-end align-bottom border-end">
              <span className="text-dark">一括支払</span>
              <br />
              実績(税抜)
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-15 text-end align-bottom">
              予定(税抜)
            </th>
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-15 text-end align-bottom border-end">
              <span className="text-dark">月額支払</span>
              <br />
              実績(税抜)
            </th>
            {/*<th className="text-uppercase text-secondary text-xxs text-dark font-weight-bolder opacity-7 px-2 w-15 text-end align-bottom">*/}
            {/*  予定(税抜)*/}
            {/*</th>*/}
            {/*<th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-15 text-end align-bottom border-end">*/}
            {/*  <span className="text-dark">支払合計</span>*/}
            {/*  <br />*/}
            {/*  予定(税抜)*/}
            {/*</th>*/}
            <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 px-2 w-15 text-end align-bottom">
              <span className="text-dark">合計</span>
              <br />
              予定・実績(税抜)
            </th>
            <th className="text-secondary opacity-7 w-5 text-end" style={{ width: 110 }}></th>
          </tr>
        </thead>
        <tbody>
          {paymentSchedules?.map((d) => (
            <PaymentScheduleRow key={`ps-${d.id}`} d={d} setDelTarget={setDelTarget} isEditable={isEditable} />
          ))}
          <tr>
            <td className="text-xs text-dark font-weight-bold text-end border-end">合計金額</td>
            <td className="text-xs text-dark font-weight-bold text-end">
              ¥{PaymentScheduleLogic.calcTotalSpotAmount(paymentSchedules || []).toLocaleString()}
            </td>
            <td className="text-xs text-dark font-weight-bold text-end border-end">
              ¥{PaymentScheduleLogic.calcTotalSpotPaymentTaskPaymentAmount(paymentSchedules || []).toLocaleString()}
            </td>
            <td className="text-xs text-dark font-weight-bold text-end">
              ¥{PaymentScheduleLogic.calcTotalMonthlyAmount(paymentSchedules || []).toLocaleString()}
            </td>
            <td className="text-xs text-dark font-weight-bold text-end border-end">
              ¥{PaymentScheduleLogic.calcTotalMonthlyPaymentTaskPaymentAmount(paymentSchedules || []).toLocaleString()}
            </td>
            {/*<td className="text-xs text-dark font-weight-bold text-end border-end">*/}
            {/*  ¥{PaymentScheduleLogic.calcTotalAmount(paymentSchedules || []).toLocaleString()}*/}
            {/*</td>*/}
            {/*<td className="text-xs text-dark font-weight-bold text-end border-end">*/}
            {/*  ¥{PaymentScheduleLogic.calcTotalPaymentTaskPaymentAmount(paymentSchedules || []).toLocaleString()}*/}
            {/*</td>*/}
            <td className="text-xs text-dark font-weight-bold text-end">
              ¥{PaymentScheduleLogic.calcTotalPaymentAmount(paymentSchedules || []).toLocaleString()}
            </td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

type FormInputs = {
  identifier: string;
  team_id: number;
  purchase_order_id: number;
  payment_schedule_id: number;
  year_month: string;
  monthly_amount?: number;
  spot_amount?: number;
};

const PaymentScheduleRow = ({
  d,
  setDelTarget,
  isEditable,
}: {
  d: PaymentSchedule;
  setDelTarget: Dispatch<SetStateAction<PaymentSchedule | undefined>>;
  isEditable: boolean;
}) => {
  const { setShowLoading } = useLoading();
  const { _w_, _t_, me, meTeamMember } = useCurrentTeamMember();
  const { _po_, mePurchaseOrderMember } = useCurrentPurchaseOrder();
  const [editing, setEditing] = useState<boolean>(false);
  const { mutate, data, error, isPending } = useUpdatePaymentSchedule();
  const { invalidate } = usePaymentSchedulesInvalidate({ identifier: _w_!.identifier, team_id: _t_!.id, id: _po_!.id });
  const { invalidate: invalidatePurchaseOrder } = usePurchaseOrderInvalidate({
    identifier: _w_!.identifier,
    team_id: _t_!.id,
    id: _po_!.id,
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      identifier: _w_!.identifier,
      team_id: _t_!.id,
      purchase_order_id: _po_!.id,
      payment_schedule_id: d.id,
      year_month: d.year_month,
      spot_amount: d.spot_detail?.amount,
      monthly_amount: d.monthly_detail?.amount,
    },
  });

  useEffect(() => setShowLoading(isPending), [isPending]);
  useEffect(() => toastApiError(error), [error]);

  const onSubmit: SubmitHandler<FormInputs> = (inputData) => {
    setShowLoading(true);

    if (d.spot_closed) {
      inputData.spot_amount = undefined;
      inputData.year_month = d.year_month;
    }

    mutate(inputData);
  };

  useEffect(() => {
    if (data) {
      invalidate();
      invalidatePurchaseOrder();
      setEditing(false);
    }
  }, [data]);

  if (!editing) {
    return (
      <tr className="">
        <td className="text-xs text-dark text-end border-end">{d.year_month}</td>
        {d.spot_detail == null ? (
          <>
            <td className="text-xs text-dark text-end">-</td>
            <td className="text-xs text-dark text-end border-end">-</td>
          </>
        ) : (
          <>
            <td className="text-xs text-dark text-end">¥{PaymentScheduleLogic.spotAmount(d).toLocaleString()}</td>
            <td className="text-xs text-dark text-end border-end">
              {d.spot_detail.payment_task_payment_amount != null ? (
                <>¥{PaymentScheduleLogic.spotPaymentTaskPaymentAmount(d).toLocaleString()}</>
              ) : (
                <>-</>
              )}
            </td>
          </>
        )}
        {d.monthly_detail == null ? (
          <>
            <td className="text-xs text-dark text-end">-</td>
            <td className="text-xs text-dark text-end border-end">-</td>
          </>
        ) : (
          <>
            <td className="text-xs text-dark text-end">¥{PaymentScheduleLogic.monthlyAmount(d).toLocaleString()}</td>
            <td className="text-xs text-dark text-end border-end">
              {d.monthly_detail.payment_task_payment_amount != null ? (
                <>¥{PaymentScheduleLogic.monthlyPaymentTaskPaymentAmount(d).toLocaleString()}</>
              ) : (
                <>-</>
              )}
            </td>
          </>
        )}
        {/*<td className="text-xs text-dark font-weight-bold text-end border-end">*/}
        {/*  ¥{PaymentScheduleLogic.totalAmount(d).toLocaleString()}*/}
        {/*</td>*/}
        {/*<td className="text-xs text-dark font-weight-bold text-end border-end">*/}
        {/*  ¥{PaymentScheduleLogic.totalPaymentTaskPaymentAmount(d).toLocaleString()}*/}
        {/*</td>*/}
        <td className="text-xs text-dark font-weight-bold text-end">
          ¥{PaymentScheduleLogic.totalPaymentAmount(d).toLocaleString()}
        </td>
        <td className="text-end">
          <button
            className="btn btn-icon-only bg-gradient-success me-1"
            onClick={() => setEditing((e) => !e)}
            disabled={!isEditable}
          >
            <i className="fas fa-edit"></i>
          </button>
          <button
            className="btn btn-icon-only bg-gradient-danger"
            data-bs-toggle="modal"
            data-bs-target={`#delConfirm`}
            onClick={() => setDelTarget((_) => d)}
            disabled={!isEditable}
          >
            <i className="fas fa-trash-alt"></i>
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="">
      <td className="text-xs text-dark text-end border-end">{d.year_month}</td>
      <td className="text-xs text-dark pe-0">
        <div className="input-group me-2">
          <Controller
            render={({ field: { onChange, value, ...field } }) => <InputPrice onChange={onChange} value={value} />}
            control={control}
            name="spot_amount"
          />
        </div>
      </td>
      <td className="text-xs text-dark text-end border-end">
        {d.spot_detail && d.spot_detail.payment_task_payment_amount != null ? (
          <>¥{PaymentScheduleLogic.spotPaymentTaskPaymentAmount(d).toLocaleString()}</>
        ) : (
          <>-</>
        )}
      </td>
      <td className="text-xs text-dark pe-0">
        <div className="input-group me-2">
          <Controller
            render={({ field: { onChange, value, ...field } }) => <InputPrice onChange={onChange} value={value} />}
            control={control}
            name="monthly_amount"
          />
        </div>
      </td>
      <td className="text-xs text-dark text-end border-end">
        {d.monthly_detail && d.monthly_detail.payment_task_payment_amount != null ? (
          <>¥{PaymentScheduleLogic.monthlyPaymentTaskPaymentAmount(d).toLocaleString()}</>
        ) : (
          <>-</>
        )}
      </td>
      {/*<td className="text-xs text-dark font-weight-bold text-end border-end">*/}
      {/*  ¥{PaymentScheduleLogic.totalAmount(d).toLocaleString()}*/}
      {/*</td>*/}
      {/*<td className="text-xs text-dark font-weight-bold text-end border-end">*/}
      {/*  ¥{PaymentScheduleLogic.totalPaymentTaskPaymentAmount(d).toLocaleString()}*/}
      {/*</td>*/}
      <td className="text-xs text-dark font-weight-bold text-end">
        ¥{PaymentScheduleLogic.totalPaymentAmount(d).toLocaleString()}
      </td>
      <td className="text-end">
        <button className="btn btn-icon-only btn-outline-secondary me-1" onClick={() => setEditing((e) => !e)}>
          <i className="fas fa-undo"></i>
        </button>
        <button className="btn btn-icon-only bg-gradient-dark" onClick={() => handleSubmit(onSubmit)()}>
          <i className="fas fa-check-circle"></i>
        </button>
      </td>
    </tr>
  );
};
